import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Divider,
  Grid2,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";
import { CustomBtn } from "../../../components/Common/CustomBtn";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalculateIcon from "@mui/icons-material/Calculate";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { colors } from "../../../theme/theme";
import { SecondaryBtn } from "../../../components/Common/CustomBtn";
import axios from "axios";
import { BASE_URL } from "../../../utils/config/config";

const PaymentModal = ({
  open,
  onClose,
  onConfirm,
  appointment,
  cardInfo,
  practitionerName,
  processing,
  amount = 150.0,
  appointmentDuration,
  hourlyRate,
  paymentSuccess = false,
  paymentData = null,
}) => {
  const [selectedEndTime, setSelectedEndTime] = React.useState("");
  const [updatingBooking, setUpdatingBooking] = React.useState(false);
  const [bookingUpdateError, setBookingUpdateError] = React.useState(null);

  const generateEndTimeOptions = () => {
    if (!appointment?.startTime) {
      console.log("❌ No appointment start time");
      return [];
    }

    const options = [];
    const startTime = new Date(appointment.startTime);
    const originalEndTime = new Date(appointment.endTime);

    console.log("📅 Generating options from start time:", startTime);
    console.log("📅 Original end time:", originalEndTime);

    for (let minutes = 15; minutes <= 240; minutes += 15) {
      const endTime = new Date(startTime.getTime() + minutes * 60 * 1000);
      options.push({
        value: endTime.toISOString(),
        label: endTime.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        duration: minutes,
        isOriginal:
          Math.abs(endTime.getTime() - originalEndTime.getTime()) < 60000,
      });
    }

    console.log("✅ Generated", options.length, "time options");
    return options;
  };

  const endTimeOptions = React.useMemo(
    () => generateEndTimeOptions(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointment?.startTime, appointment?.endTime]
  );

  React.useEffect(() => {
    if (appointment?.endTime && open) {
      console.log("🔧 Setting default end time to:", appointment.endTime);
      setSelectedEndTime(appointment.endTime);
    }
  }, [appointment?.endTime, open]);

  React.useEffect(() => {
    if (!open) {
      setSelectedEndTime("");
      setBookingUpdateError(null);
    }
  }, [open]);

  const calculateActualDuration = () => {
    if (!selectedEndTime || !appointment?.startTime) return appointmentDuration;

    const start = new Date(appointment.startTime);
    const end = new Date(selectedEndTime);
    const durationInMinutes = Math.round((end - start) / (1000 * 60));

    return durationInMinutes > 0 ? durationInMinutes : appointmentDuration;
  };

  // Calculate actual amount based on selected end time
  const calculateActualAmount = () => {
    if (!selectedEndTime || !hourlyRate) return amount;

    const duration = calculateActualDuration();
    return (duration / 60) * hourlyRate;
  };

  const actualDuration = calculateActualDuration();
  const actualAmount = calculateActualAmount();
  const hasEndTimeChanged =
    selectedEndTime && selectedEndTime !== appointment?.endTime;

  console.log("🔍 Modal State:");
  console.log("  - Options count:", endTimeOptions.length);
  console.log("  - Selected end time:", selectedEndTime);
  console.log("  - Has changed:", hasEndTimeChanged);
  console.log("  - Actual duration:", actualDuration);

  const updateCalendarEventEndTime = async () => {
    if (!selectedEndTime || !appointment?.id || !hasEndTimeChanged) {
      return true;
    }

    if (!appointment.calendarId) {
      console.error("❌ No calendarId found in appointment");
      setBookingUpdateError("Calendar ID is missing from session data");
      return false;
    }

    try {
      setUpdatingBooking(true);
      setBookingUpdateError(null);

      const requestBody = {
        calendarId: appointment.calendarId,
        title: appointment.title,
        appointmentStatus: appointment.appointmentStatus || "confirmed",
        startTime: appointment.startTime,
        endTime: selectedEndTime,
        // Include other fields if they exist
        ...(appointment.address && { address: appointment.address }),
        ...(appointment.contactId && { contactId: appointment.contactId }),
        ...(appointment.assignedUserId && {
          assignedUserId: appointment.assignedUserId,
        }),
      };

      console.log("=== UPDATE CALENDAR EVENT ===");
      console.log("📅 Event ID:", appointment.id);
      console.log("📅 Calendar ID:", appointment.calendarId);
      console.log("📊 Request body:", JSON.stringify(requestBody, null, 2));
      console.log("📊 New end time:", selectedEndTime);
      console.log("📊 Original end time:", appointment.endTime);

      const response = await axios.put(
        `${BASE_URL}gohighlevel/events/${appointment.id}`,
        requestBody
      );

      console.log("✅ Response:", response.data);

      if (response.data.success) {
        console.log("✅ Calendar event updated successfully");
        setUpdatingBooking(false);
        return true;
      } else {
        throw new Error(
          response.data.message || "Failed to update calendar event"
        );
      }
    } catch (error) {
      console.error("❌ Error updating calendar event:", error);
      console.error("❌ Error response:", error.response?.data);
      console.error("❌ Error status:", error.response?.status);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update session time. Please try again.";

      setBookingUpdateError(errorMessage);
      setUpdatingBooking(false);
      return false;
    }
  };

  // Enhanced confirm handler with booking update
  const handleConfirmWithBookingUpdate = async () => {
    try {
      // Step 1: Update calendar event if end time changed
      if (hasEndTimeChanged) {
        console.log("🔄 End time changed, updating calendar event first...");
        const updateSuccess = await updateCalendarEventEndTime();

        if (!updateSuccess) {
          console.error(
            "❌ Calendar event update failed, stopping payment process"
          );
          return;
        }
      }

      // Step 2: Process payment
      console.log("💳 Proceeding with payment...");
      await onConfirm();
    } catch (error) {
      console.error("❌ Error in payment flow:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={processing || updatingBooking ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 4,
          backgroundColor: paymentSuccess ? "#4CAF50" : colors.primary,
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {paymentSuccess ? (
            <CheckCircleIcon sx={{ fontSize: 28 }} />
          ) : (
            <AttachMoneyIcon sx={{ fontSize: 28 }} />
          )}
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontFamily: "'Gotham', sans-serif",
              fontWeight: 700,
            }}
          >
            {paymentSuccess ? "Payment Successful" : "Charge Client"}
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3, px: 4, pb: 2, backgroundColor: "#FAFAFA" }}>
        {processing || updatingBooking ? (
          <Box sx={{ textAlign: "center", py: 8, px: 4 }}>
            <CircularProgress
              size={64}
              thickness={4}
              sx={{ color: colors.primaryText, mb: 3 }}
            />
            <Typography
              variant="h6"
              sx={{
                color: colors.primary,
                fontWeight: 600,
                fontFamily: "'Gotham', sans-serif",
                mb: 1,
              }}
            >
              {updatingBooking
                ? "Updating Session..."
                : "Processing Payment..."}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.primary,
                fontFamily: "'Gotham', sans-serif",
                opacity: 0.8,
              }}
            >
              {updatingBooking
                ? "Updating session end time"
                : "Please wait while we charge the client's card"}
            </Typography>
          </Box>
        ) : paymentSuccess ? (
          <Box>
            <Grid2 container spacing={3}>
              <Grid2 item size={{ xs: 12 }}>
                <Box
                  sx={{
                    textAlign: "center",
                    py: 3,
                    px: 4,
                    border: `2px solid #4CAF50`,
                    borderRadius: "14px",
                    backgroundColor: "rgba(76, 175, 80, 0.05)",
                  }}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: 64, color: "#4CAF50", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: colors.primary,
                      mb: 1,
                      fontFamily: "'Gotham', sans-serif",
                    }}
                  >
                    Payment Processed Successfully!
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.primary,
                      fontFamily: "'Gotham', sans-serif",
                    }}
                  >
                    The client has been charged and will receive a confirmation
                    email shortly.
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>
          </Box>
        ) : cardInfo && appointment ? (
          <Box>
            <Grid2 container spacing={3}>
              {/* Booking Update Error */}
              {bookingUpdateError && (
                <Grid2 item size={{ xs: 12 }}>
                  <Alert
                    severity="error"
                    sx={{
                      borderRadius: "14px",
                      fontFamily: "'Gotham', sans-serif",
                    }}
                    onClose={() => setBookingUpdateError(null)}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Failed to update session time
                    </Typography>
                    <Typography variant="body2">
                      {bookingUpdateError}
                    </Typography>
                  </Alert>
                </Grid2>
              )}

              {/* Client Information */}
              <Grid2 item size={{ xs: 12 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <PersonIcon sx={{ color: colors.primary, fontSize: 22 }} />
                  <Typography
                    sx={{
                      fontFamily: "'Gotham', sans-serif",
                      fontSize: "18px",
                      color: colors.primary,
                    }}
                  >
                    Client Information
                  </Typography>
                </Box>
                <Box
                  sx={{
                    border: `1px solid ${colors.primary}20`,
                    borderRadius: "14px",
                    backgroundColor: `${colors.primary}05`,
                    p: 3,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: colors.primary,
                      fontFamily: "'Gotham', sans-serif",
                    }}
                  >
                    {appointment?.title || appointment?.contact?.name || "N/A"}
                  </Typography>
                </Box>
              </Grid2>

              {/* Appointment Time Selection */}
              <Grid2 item size={{ xs: 12 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <CalendarTodayIcon
                    sx={{ color: colors.primary, fontSize: 22 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "'Gotham', sans-serif",
                      fontSize: "18px",
                      color: colors.primary,
                    }}
                  >
                    Session Time
                  </Typography>
                </Box>
                <Box
                  sx={{
                    border: `1px solid ${colors.primary}20`,
                    borderRadius: "14px",
                    backgroundColor: `${colors.primary}05`,
                    p: 3,
                  }}
                >
                  <Grid2 container spacing={2}>
                    <Grid2 item size={{ xs: 12, sm: 6 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.primary,
                          display: "block",
                          mb: 0.5,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                          fontFamily: "'Gotham', sans-serif",
                        }}
                      >
                        Start Time
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: colors.primary,
                          fontFamily: "'Gotham', sans-serif",
                        }}
                      >
                        {appointment?.startTime
                          ? new Date(appointment.startTime).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                                timeZoneName: "short",
                              }
                            )
                          : "N/A"}
                      </Typography>
                    </Grid2>

                    <Grid2 item size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel
                          sx={{
                            fontFamily: "'Gotham', sans-serif",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            color: colors.primary,
                            "&.Mui-focused": { color: colors.primary },
                          }}
                        >
                          End Time
                        </InputLabel>
                        <Select
                          value={selectedEndTime}
                          onChange={(e) => {
                            console.log(
                              "🔄 End time selected:",
                              e.target.value
                            );
                            setSelectedEndTime(e.target.value);
                          }}
                          label="End Time"
                          sx={{
                            fontFamily: "'Gotham', sans-serif",
                            fontSize: "14px",
                            color: colors.primary, // Brown text color
                            "& .MuiSelect-select": {
                              color: colors.primary, // Brown text for selected value
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: `${colors.primary}40`,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.primary,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.primary,
                            },
                          }}
                          startAdornment={
                            <ScheduleIcon
                              sx={{
                                fontSize: 18,
                                color: colors.primary,
                                mr: 1,
                              }}
                            />
                          }
                        >
                          {endTimeOptions.length === 0 ? (
                            <MenuItem disabled>No options available</MenuItem>
                          ) : (
                            endTimeOptions.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                  fontFamily: "'Gotham', sans-serif",
                                  fontSize: "14px",
                                  backgroundColor: option.isOriginal
                                    ? `${colors.primary}10`
                                    : "transparent",
                                  "&:hover": {
                                    backgroundColor: `${colors.primary}20`,
                                  },
                                }}
                              >
                                {option.label} ({option.duration} min)
                                {option.isOriginal && " - Original"}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid2>

                    <Grid2 item size={{ xs: 12 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <AccessTimeIcon
                          sx={{ fontSize: 16, color: colors.primary }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: hasEndTimeChanged
                              ? "#FF9800"
                              : colors.primary,
                            fontFamily: "'Gotham', sans-serif",
                          }}
                        >
                          Duration: {actualDuration} minutes
                          {hasEndTimeChanged && (
                            <span
                              style={{
                                fontSize: "0.85em",
                                opacity: 0.7,
                                marginLeft: "4px",
                              }}
                            >
                              (modified)
                            </span>
                          )}
                        </Typography>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
              </Grid2>

              {/* Charge Amount */}
              <Grid2 item size={{ xs: 12 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <CalculateIcon sx={{ color: colors.primary, fontSize: 22 }} />
                  <Typography
                    sx={{
                      fontFamily: "'Gotham', sans-serif",
                      fontSize: "18px",
                      color: colors.primary,
                    }}
                  >
                    Charge Amount
                  </Typography>
                </Box>
                <Box
                  sx={{
                    border: `2px solid ${colors.primary}`,
                    borderRadius: "14px",
                    backgroundColor: `${colors.primary}15`,
                    p: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "'Gotham', sans-serif",
                          color: colors.primary,
                          mb: 1,
                        }}
                      >
                        {actualDuration} minutes × ${hourlyRate}/hour
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          fontStyle: "italic",
                          fontSize: "0.65rem",
                          fontFamily: "'Gotham', sans-serif",
                          mb: 1,
                        }}
                      >
                        Note: Each clock hour represents 50 minutes of session
                        time within a 60-minute scheduling block.
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.primary,
                          display: "block",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                          fontFamily: "'Gotham', sans-serif",
                        }}
                      >
                        Total Charge
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          color: colors.primary,
                          fontWeight: 800,
                          fontFamily: "'Gotham', sans-serif",
                        }}
                      >
                        ${actualAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <AttachMoneyIcon
                      sx={{ fontSize: 48, color: colors.primary, opacity: 0.3 }}
                    />
                  </Box>
                </Box>
              </Grid2>

              {/* Payment Method */}
              <Grid2 item size={{ xs: 12 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CreditCardIcon
                    sx={{ color: colors.primary, fontSize: 20 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "'Gotham', sans-serif",
                      color: colors.primary,
                    }}
                  >
                    {cardInfo.cardBrand} •••• {cardInfo.last4}
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 6, px: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#C62828",
                fontWeight: 600,
                fontFamily: "'Quicksand', sans-serif",
              }}
            >
              Unable to load payment information
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2, backgroundColor: "#FAFAFA" }}>
        {paymentSuccess ? (
          <CustomBtn
            text="Close"
            onClick={onClose}
            noIcon
            sx={{
              backgroundColor: "#8B4513",
              color: "white",
              fontWeight: 700,
              px: 4,
              py: 1,
              borderRadius: "14px",
              fontFamily: "'Quicksand', sans-serif",
              "&:hover": { backgroundColor: "#A0522D", opacity: 0.9 },
            }}
          />
        ) : (
          <>
            <CustomBtn
              text="Cancel"
              onClick={onClose}
              disabled={processing || updatingBooking}
              noIcon
              sx={{
                borderColor: colors.primary,
                color: colors.primary,
                fontWeight: 600,
                px: 4,
                py: 1,
                borderRadius: "14px",
                fontFamily: "'Gotham', sans-serif",
                "&:hover": {
                  borderColor: colors.primaryText,
                  backgroundColor: `${colors.primaryText}10`,
                },
              }}
            />
            <SecondaryBtn
              smallR
              text={
                updatingBooking
                  ? "Updating..."
                  : processing
                  ? "Processing..."
: hasEndTimeChanged
                   ? "Update & Charge"
                  : "Charge Client"
              }
              iconProps={
                processing || updatingBooking
                  ? { size: 20, sx: { color: "white" } }
                  : {}
              }
              width="50%"
              onClick={handleConfirmWithBookingUpdate}
              disabled={processing || updatingBooking || !cardInfo}
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;
